import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { schedulingAPI, allocationsAPI } from '../../services/api';
import { formatCalendarDate } from '../../utils/dateTimeUtils';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import BatchCreationForm from '../../components/BatchCreationForm';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_INDICES = [0, 1, 2, 3, 4, 5, 6];

const DAY_HEADER_COLORS = [
  '#e3f2fd', '#fff3e0', '#e8f5e9', '#fce4ec', '#f3e5f5', '#e0f7fa', '#fff8e1'
];

function formatDate(d) {
  if (!d) return '';
  return formatCalendarDate(d);
}

function toHHmm(v) {
  if (!v || typeof v !== 'string') return '';
  const match = String(v).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
}

export default function Scheduling() {
  const queryClient = useQueryClient();
  const [studentId, setStudentId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [win1Start, setWin1Start] = useState('');
  const [win1End, setWin1End] = useState('');
  const [win2Start, setWin2Start] = useState('');
  const [win2End, setWin2End] = useState('');
  const [optionalFiltersOpen, setOptionalFiltersOpen] = useState(false);
  const [filterDays, setFilterDays] = useState([]);
  const [filterTeachers, setFilterTeachers] = useState([]);
  const [filterTimeStart, setFilterTimeStart] = useState('');
  const [filterTimeEnd, setFilterTimeEnd] = useState('');
  const [daysPerWeek, setDaysPerWeek] = useState(2);

  // Teacher grid selection state
  const [selectedTeacherSlots, setSelectedTeacherSlots] = useState(new Map()); // teacherId -> Set of colKeys

  useEffect(() => {
    if (!studentId) {
      setWin1Start('');
      setWin1End('');
      setWin2Start('');
      setWin2End('');
      setFilterDays([]);
      setFilterTeachers([]);
      setFilterTimeStart('');
      setFilterTimeEnd('');
      setDaysPerWeek(2);
    }
  }, [studentId]);

  const gridParams = useMemo(() => {
    const windows = [];
    if (win1Start && win1End) windows.push({ start: toHHmm(win1Start), end: toHHmm(win1End) });
    if (win2Start && win2End) windows.push({ start: toHHmm(win2Start), end: toHHmm(win2End) });
    const timeWindows = windows.length ? JSON.stringify(windows) : null;
    return {
      studentId,
      timeWindows,
      days: filterDays.length ? filterDays.join(',') : '',
      teacherIds: filterTeachers.map((t) => t.id).filter(Boolean).join(','),
      timeOfDayStart: toHHmm(filterTimeStart) || '',
      timeOfDayEnd: toHHmm(filterTimeEnd) || '',
    };
  }, [studentId, win1Start, win1End, win2Start, win2End, filterDays, filterTeachers, filterTimeStart, filterTimeEnd]);

  const suggestionsParams = useMemo(
    () => ({ studentId, daysPerWeek }),
    [studentId, daysPerWeek]
  );

  const hasBroadWindow = !!(win1Start && win1End);

  const { data: unassignedRes, isLoading: loadingStudents } = useQuery(
    ['scheduling-unassigned', search, page],
    () => schedulingAPI.getUnassignedStudents({ page, limit: 50, search: search || undefined }),
    { keepPreviousData: true }
  );
  const students = unassignedRes?.data?.data?.students ?? [];
  const pagination = unassignedRes?.data?.data?.pagination ?? { total: 0, totalPages: 0 };

  const { data: profileRes, isLoading: loadingProfile } = useQuery(
    ['scheduling-profile', studentId],
    () => schedulingAPI.getStudentProfile(studentId),
    { enabled: !!studentId }
  );
  const profile = profileRes?.data?.data;
  const profileComplete = profile?.profileComplete ?? false;
  const canFetchSuggestions = !!studentId && profileComplete;
  const canFetchGrids = !!studentId && profileComplete && hasBroadWindow && !!gridParams.timeWindows;

  const { data: batchGridRes, isLoading: loadingBatchGrid } = useQuery(
    ['scheduling-batch-grid', gridParams],
    () => schedulingAPI.getBatchGrid(gridParams),
    { enabled: canFetchGrids, keepPreviousData: true }
  );
  const batchGrid = batchGridRes?.data?.data ?? {};
  const { rows: batchRows, columns: batchCols, cells: batchCells, oneOnOne } = batchGrid;

  const { data: teacherGridRes, isLoading: loadingTeacherGrid } = useQuery(
    ['scheduling-teacher-grid', gridParams],
    () => schedulingAPI.getTeacherGrid(gridParams),
    { enabled: canFetchGrids, keepPreviousData: true }
  );
  const teacherGrid = teacherGridRes?.data?.data ?? {};
  const { rows: teacherRows, columns: teacherCols, cells: teacherCells } = teacherGrid;

  const { data: suggestionsRes, isLoading: loadingSuggestions } = useQuery(
    ['scheduling-suggestions', suggestionsParams],
    () => schedulingAPI.getSuggestions(suggestionsParams),
    { enabled: canFetchSuggestions, keepPreviousData: true }
  );
  const suggestionsData = suggestionsRes?.data?.data ?? {};
  const {
    existingBatches = [],
    newBatchCandidates = [],
    oneOnOne: suggestionsOneOnOne,
    profileMissingAvailability: suggestionsMissingAvailability = false
  } = suggestionsData;

  const teacherOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const r of batchRows || []) {
      const id = r.teacherId || r.teacher_id;
      const name = r.teacherName || '';
      if (id && !seen.has(id)) {
        seen.add(id);
        list.push({ id, name });
      }
    }
    for (const r of teacherRows || []) {
      const id = r.teacherId || r.id;
      const name = r.teacherName || '';
      if (id && !seen.has(id)) {
        seen.add(id);
        list.push({ id, name });
      }
    }
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [batchRows, teacherRows]);

  const cellMapBatch = useMemo(() => {
    const m = new Map();
    (batchCells || []).forEach((c) => m.set(`${c.rowId}|${c.colKey}`, c));
    return m;
  }, [batchCells]);

  const cellMapTeacher = useMemo(() => {
    const m = new Map();
    (teacherCells || []).forEach((c) => m.set(`${c.rowId}|${c.colKey}`, c));
    return m;
  }, [teacherCells]);

  /** colKey -> { istTime, studentTime } from first cell per column */
  const columnMetaBatch = useMemo(() => {
    const m = new Map();
    (batchCells || []).forEach((c) => {
      if (!m.has(c.colKey)) m.set(c.colKey, { istTime: c.istTime, studentTime: c.studentTime });
    });
    return m;
  }, [batchCells]);

  const columnMetaTeacher = useMemo(() => {
    const m = new Map();
    (teacherCells || []).forEach((c) => {
      if (!m.has(c.colKey)) m.set(c.colKey, { istTime: c.istTime, studentTime: c.studentTime });
    });
    return m;
  }, [teacherCells]);

  /** Group columns by day for header row 1 (day) + row 2 (IST+student per slot) */
  const batchHeaderGroups = useMemo(() => {
    const cols = batchCols || [];
    const groups = [];
    let i = 0;
    while (i < cols.length) {
      const day = cols[i].day_of_week;
      const start = i;
      while (i < cols.length && cols[i].day_of_week === day) i++;
      groups.push({ day, cols: cols.slice(start, i) });
    }
    return groups;
  }, [batchCols]);

  const teacherHeaderGroups = useMemo(() => {
    const cols = teacherCols || [];
    const groups = [];
    let i = 0;
    while (i < cols.length) {
      const day = cols[i].day_of_week;
      const start = i;
      while (i < cols.length && cols[i].day_of_week === day) i++;
      groups.push({ day, cols: cols.slice(start, i) });
    }
    return groups;
  }, [teacherCols]);

  const [assigning, setAssigning] = useState(null);
  const [createBatchDialogOpen, setCreateBatchDialogOpen] = useState(false);
  const [createBatchPrefill, setCreateBatchPrefill] = useState(null);

  const handleAssign = async (batchId) => {
    if (!studentId || !batchId) return;
    setAssigning(batchId);
    try {
      await allocationsAPI.assignStudent({
        studentId,
        batchId,
        assignmentType: 'manual',
      });
      toast.success('Student assigned to batch successfully');
      queryClient.invalidateQueries(['scheduling-unassigned']);
      queryClient.invalidateQueries(['scheduling-suggestions', suggestionsParams]);
      setStudentId('');
      setAssigning(null);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Assign failed';
      toast.error(msg);
      setAssigning(null);
    }
  };

  const handleCreateFromSuggestion = (c) => {
    const schedules = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    (c.schedules || []).forEach((s) => {
      const d = s.day_of_week;
      if (d >= 0 && d <= 6) {
        const startTime = s.start_time ? String(s.start_time).substring(0, 5) : '';
        const endTime = s.end_time ? String(s.end_time).substring(0, 5) : '';
        if (startTime && endTime) schedules[d].push({ startTime, endTime });
      }
    });
    const is1on1 = profile?.class_type_preference === 'one_on_one';
    setCreateBatchPrefill({
      teacher_id: c.teacherId,
      age_group: profile?.age_group || '',
      batch_type: is1on1 ? 'one_on_one' : 'group',
      max_capacity: is1on1 ? 1 : undefined,
      start_date: c.suggestedStartDate ? c.suggestedStartDate.slice(0, 10) : undefined,
      schedules,
    });
    setCreateBatchDialogOpen(true);
  };

  // Teacher grid slot selection
  const handleTeacherSlotToggle = (teacherId, colKey) => {
    setSelectedTeacherSlots(prev => {
      const newMap = new Map(prev);
      const teacherSlots = newMap.get(teacherId) || new Set();

      if (teacherSlots.has(colKey)) {
        teacherSlots.delete(colKey);
      } else {
        teacherSlots.add(colKey);
      }

      if (teacherSlots.size === 0) {
        newMap.delete(teacherId);
      } else {
        newMap.set(teacherId, teacherSlots);
      }

      return newMap;
    });
  };

  const handleCreateBatchClick = () => {
    if (!studentId || selectedTeacherSlots.size === 0) return;
    const teacherId = Array.from(selectedTeacherSlots.keys())[0];
    const selectedSlots = Array.from(selectedTeacherSlots.get(teacherId));

    if (selectedSlots.length !== 2) {
      toast.error('Please select exactly 2 slots for batch creation');
      return;
    }

    const selectedSlotDetails = selectedSlots.map(colKey => {
      const cell = (teacherCells || []).find(c => c.colKey === colKey && c.teacherId === teacherId);
      return cell ? { day_of_week: cell.day_of_week, start_time: cell.start_time, end_time: cell.end_time } : null;
    }).filter(Boolean);

    if (selectedSlotDetails.length !== 2) {
      toast.error('Could not find slot details');
      return;
    }

    const schedules = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    selectedSlotDetails.forEach(s => {
      const day = s.day_of_week;
      if (day >= 0 && day <= 6) {
        const startTime = s.start_time ? String(s.start_time).substring(0, 5) : '';
        const endTime = s.end_time ? String(s.end_time).substring(0, 5) : '';
        if (startTime && endTime) schedules[day].push({ startTime, endTime });
      }
    });

    setCreateBatchPrefill({
      teacher_id: teacherId,
      age_group: profile?.age_group || '',
      batch_type: profile?.class_type_preference === 'one_on_one' ? 'one_on_one' : 'group',
      schedules,
    });
    setCreateBatchDialogOpen(true);
    setSelectedTeacherSlots(new Map());
  };

  const handleCreateBatchSuccess = () => {
    queryClient.invalidateQueries(['batches']);
    queryClient.invalidateQueries(['scheduling-batch-grid', gridParams]);
    queryClient.invalidateQueries(['scheduling-teacher-grid', gridParams]);
    queryClient.invalidateQueries(['scheduling-suggestions', suggestionsParams]);
    setCreateBatchDialogOpen(false);
    setCreateBatchPrefill(null);
    toast.success('Batch created successfully');
  };

  const loadingGrids = loadingBatchGrid || loadingTeacherGrid;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Batch scheduling
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select an unassigned student and classes per week to see recommendations (from profile availability). Use broad
        time windows and optional filters only for manual grid selection.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            1. Select student
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, parent, email, phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Student</InputLabel>
              <Select
                value={studentId}
                label="Student"
                onChange={(e) => setStudentId(e.target.value)}
                displayEmpty
                disabled={loadingStudents}
              >
                <MenuItem value="">
                  <em>Select unassigned student</em>
                </MenuItem>
                {students.map((s) => (
                  <MenuItem key={s.student_id} value={s.student_id}>
                    {[s.first_name, s.last_name].filter(Boolean).join(' ') || s.student_email || s.student_id}
                    {s.age_group ? ` · ${s.age_group}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Prev
                </Button>
                <Typography variant="body2">
                  {page} / {pagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {studentId && (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                2. Student profile (from profile)
              </Typography>
              {loadingProfile ? (
                <CircularProgress size={20} />
              ) : profile ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Country: ${profile.country || '—'}`} size="small" variant="outlined" />
                  <Chip label={`Timezone: ${profile.timezone || '—'}`} size="small" variant="outlined" />
                  <Chip label={`Age group: ${profile.age_group || '—'}`} size="small" variant="outlined" />
                  <Chip
                    label={`Preference: ${profile.class_type_preference === 'one_on_one' ? 'One-on-one' : 'Group'}`}
                    size="small"
                    variant="outlined"
                  />
                  {!profileComplete && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Profile incomplete. Please set timezone, age group, and class preference in the{' '}
                      <Link to="/students">Students</Link> section.
                    </Alert>
                  )}
                </Box>
              ) : null}
            </CardContent>
          </Card>

          {profileComplete && (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    3. Recommendations (from profile availability)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Classes per week. Suggestions use the student&apos;s profile availability (time preferences).
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Classes per week</InputLabel>
                      <Select
                        value={daysPerWeek}
                        label="Classes per week"
                        onChange={(e) => setDaysPerWeek(Number(e.target.value))}
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Suggested for you
                  </Typography>
                  {loadingSuggestions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : suggestionsMissingAvailability ? (
                    <Typography variant="body2" color="text.secondary">
                      Add availability in the student&apos;s profile to see recommendations.
                    </Typography>
                  ) : existingBatches.length === 0 && newBatchCandidates.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No best fit found. Use manual selection below (broad time windows + grids).
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {existingBatches.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Suggested existing batches
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {existingBatches.map((b) => (
                              <Paper key={b.batchId} variant="outlined" sx={{ p: 1.5, minWidth: 260, maxWidth: 360 }}>
                                <Typography variant="subtitle2">
                                  {b.batchName} · {b.teacherName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {b.studentTime} · {b.istTime}
                                </Typography>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  {b.whyThisPick}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={assigning !== null}
                                  onClick={() => handleAssign(b.batchId)}
                                  sx={{ mt: 1 }}
                                >
                                  {assigning === b.batchId ? 'Assigning…' : 'Assign'}
                                </Button>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {newBatchCandidates.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {suggestionsOneOnOne ? 'Suggested 1:1 options' : 'Suggested new batches'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {newBatchCandidates.map((c, idx) => (
                              <Paper key={`suggestion-new-${idx}-${c.teacherId}`} variant="outlined" sx={{ p: 1.5, minWidth: 260, maxWidth: 360 }}>
                                <Typography variant="subtitle2">{c.teacherName}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {(c.schedules || []).map((s) => `${DAYS[s.day_of_week]} ${s.start_time?.slice(0, 5) || ''}-${s.end_time?.slice(0, 5) || ''}`).join(' · ')}
                                </Typography>
                                {c.density != null && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {c.density} matching student(s)
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  {c.whyThisPick}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleCreateFromSuggestion(c)}
                                  sx={{ mt: 1 }}
                                >
                                  {suggestionsOneOnOne ? 'Create 1:1 Batch' : 'Create Batch'}
                                </Button>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    4. Manual selection — broad time windows (student local)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Set 1–2 windows (e.g. 3 PM–8 PM) to view batch/teacher grids. Optional filters below.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Window 1</Typography>
                      <TextField
                        size="small"
                        type="time"
                        value={win1Start}
                        onChange={(e) => setWin1Start(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                      />
                      <Typography variant="body2">–</Typography>
                      <TextField
                        size="small"
                        type="time"
                        value={win1End}
                        onChange={(e) => setWin1End(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Window 2 (optional)</Typography>
                      <TextField
                        size="small"
                        type="time"
                        value={win2Start}
                        onChange={(e) => setWin2Start(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                        placeholder="Start"
                      />
                      <Typography variant="body2">–</Typography>
                      <TextField
                        size="small"
                        type="time"
                        value={win2End}
                        onChange={(e) => setWin2End(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Button
                    size="small"
                    onClick={() => setOptionalFiltersOpen((o) => !o)}
                    sx={{ textTransform: 'none', mb: optionalFiltersOpen ? 1 : 0 }}
                  >
                    {optionalFiltersOpen ? '▼' : '▶'} Optional filters for manual selection (days, teachers, time of day)
                  </Button>
                  <Collapse in={optionalFiltersOpen}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                          Preferred days
                        </Typography>
                        <FormGroup row>
                          {DAY_INDICES.map((d) => (
                            <FormControlLabel
                              key={d}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={filterDays.includes(d)}
                                  onChange={(e) =>
                                    setFilterDays((prev) =>
                                      e.target.checked ? [...prev, d].sort((a, b) => a - b) : prev.filter((x) => x !== d)
                                    )
                                  }
                                />
                              }
                              label={DAYS[d]}
                              labelPlacement="end"
                            />
                          ))}
                        </FormGroup>
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                          Preferred teachers
                        </Typography>
                        <Autocomplete
                          multiple
                          size="small"
                          options={teacherOptions}
                          getOptionLabel={(o) => (o && o.name) || ''}
                          isOptionEqualToValue={(a, b) => (a && b && a.id === b.id)}
                          value={filterTeachers}
                          onChange={(_, v) => setFilterTeachers(v || [])}
                          renderInput={(params) => <TextField {...params} placeholder="Any" />}
                          sx={{ minWidth: 220 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                          Time of day (optional)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            type="time"
                            value={filterTimeStart}
                            onChange={(e) => setFilterTimeStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            sx={{ width: 110 }}
                          />
                          <Typography variant="body2">–</Typography>
                          <TextField
                            size="small"
                            type="time"
                            value={filterTimeEnd}
                            onChange={(e) => setFilterTimeEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            sx={{ width: 110 }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>

              {!hasBroadWindow && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Set at least one broad time window (start and end) above to view availability grids.
                </Alert>
              )}

              {oneOnOne && hasBroadWindow && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  One-on-one preference: only teacher availability grid is shown.
                </Alert>
              )}

              {!oneOnOne && hasBroadWindow && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Batch-wise availability
                    </Typography>
                    {loadingGrids ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                rowSpan={2}
                                sx={{
                                  verticalAlign: 'middle',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 3,
                                  backgroundColor: '#f5f5f5',
                                  borderRight: '3px solid',
                                  borderRightColor: 'primary.main',
                                  fontWeight: 600
                                }}
                              >
                                Batch · Teacher
                              </TableCell>
                              {batchHeaderGroups.map((g, index) => (
                                <TableCell
                                  key={g.cols[0]?.key ?? g.day}
                                  align="center"
                                  colSpan={g.cols.length}
                                  sx={{
                                    backgroundColor: DAY_HEADER_COLORS[g.day % DAY_HEADER_COLORS.length],
                                    fontWeight: 600,
                                    borderLeft: index > 0 ? '3px solid' : 'none',
                                    borderLeftColor: 'primary.main'
                                  }}
                                >
                                  {DAYS[g.day]}
                                </TableCell>
                              ))}
                            </TableRow>
                            <TableRow>
                              {(batchCols || []).map((col, index) => {
                                const meta = columnMetaBatch.get(col.key);
                                const isFirstInDay = batchCols[index - 1]?.day_of_week !== col.day_of_week;
                                return (
                                  <TableCell
                                    key={col.key}
                                    align="center"
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      backgroundColor: '#fafafa',
                                      borderLeft: isFirstInDay && index > 0 ? '3px solid' : 'none',
                                      borderLeftColor: 'primary.main'
                                    }}
                                  >
                                    {meta ? (
                                      <Box sx={{ fontSize: '0.75rem' }}>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          {meta.istTime}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          {meta.studentTime}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(batchRows || []).map((row, rowIndex) => (
                              <TableRow key={row.id} sx={{ backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    backgroundColor: rowIndex % 2 === 0 ? '#f5f5f5' : '#eeeeee',
                                    borderRight: '3px solid',
                                    borderRightColor: 'primary.main'
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {row.batchName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {row.teacherName}
                                    </Typography>
                                  </Box>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 0.5 }}
                                    disabled={!!assigning}
                                    onClick={() => handleAssign(row.batchId)}
                                  >
                                    {assigning === row.batchId ? 'Assigning…' : 'Assign'}
                                  </Button>
                                </TableCell>
                                {(batchCols || []).map((col, index) => {
                                  const c = cellMapBatch.get(`${row.id}|${col.key}`);
                                  const isFirstInDay = batchCols[index - 1]?.day_of_week !== col.day_of_week;
                                  return (
                                    <TableCell
                                      key={col.key}
                                      align="center"
                                      sx={{
                                        backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#f9f9f9',
                                        borderLeft: isFirstInDay && index > 0 ? '3px solid' : 'none',
                                        borderLeftColor: 'primary.main'
                                      }}
                                    >
                                      {c ? (
                                        <Box sx={{ fontSize: '0.8rem' }}>
                                          <Typography variant="caption" display="block" fontWeight={500}>
                                            {c.studentTime}
                                          </Typography>
                                          <Typography variant="caption" display="block" color="text.secondary">
                                            {c.seatsAvailable} seat{c.seatsAvailable !== 1 ? 's' : ''} · Start{' '}
                                            {formatDate(c.startDate)}
                                          </Typography>
                                        </Box>
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                            {(!batchRows || batchRows.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={(batchCols?.length ?? 0) + 1} align="center">
                                  No matching slots found. Try removing some filters or widening the time window.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}

              {hasBroadWindow && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1">Teacher-wise availability</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select 2 slots (same teacher) to create a new batch; the form opens with teacher and times pre-filled.
                      </Typography>
                    </Box>
                    {selectedTeacherSlots.size > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleCreateBatchClick}
                      >
                        Create Batch ({Array.from(selectedTeacherSlots.values()).reduce((sum, slots) => sum + slots.size, 0)} slots)
                      </Button>
                    )}
                  </Box>
                  {loadingGrids ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              rowSpan={2}
                              sx={{
                                verticalAlign: 'middle',
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                backgroundColor: '#f5f5f5',
                                borderRight: '3px solid',
                                borderRightColor: 'primary.main',
                                fontWeight: 600
                              }}
                            >
                              Teacher
                            </TableCell>
                            {teacherHeaderGroups.map((g, index) => (
                              <TableCell
                                key={g.cols[0]?.key ?? g.day}
                                align="center"
                                colSpan={g.cols.length}
                                sx={{
                                  backgroundColor: DAY_HEADER_COLORS[g.day % DAY_HEADER_COLORS.length],
                                  fontWeight: 600,
                                  borderLeft: index > 0 ? '3px solid' : 'none',
                                  borderLeftColor: 'primary.main'
                                }}
                              >
                                {DAYS[g.day]}
                              </TableCell>
                            ))}
                          </TableRow>
                          <TableRow>
                            {(teacherCols || []).map((col, index) => {
                              const meta = columnMetaTeacher.get(col.key);
                              const isFirstInDay = teacherCols[index - 1]?.day_of_week !== col.day_of_week;
                              return (
                                <TableCell
                                  key={col.key}
                                  align="center"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    backgroundColor: '#fafafa',
                                    borderLeft: isFirstInDay && index > 0 ? '3px solid' : 'none',
                                    borderLeftColor: 'primary.main'
                                  }}
                                >
                                  {meta ? (
                                    <Box sx={{ fontSize: '0.75rem' }}>
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        {meta.istTime}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        {meta.studentTime}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(teacherRows || []).map((row, rowIndex) => (
                            <TableRow key={row.id} sx={{ backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                              <TableCell
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 2,
                                  backgroundColor: rowIndex % 2 === 0 ? '#f5f5f5' : '#eeeeee',
                                  borderRight: '3px solid',
                                  borderRightColor: 'primary.main'
                                }}
                              >
                                <Typography variant="body2" fontWeight={500}>{row.teacherName}</Typography>
                              </TableCell>
                              {(teacherCols || []).map((col, index) => {
                                const c = cellMapTeacher.get(`${row.id}|${col.key}`);
                                const isFirstInDay = teacherCols[index - 1]?.day_of_week !== col.day_of_week;
                                const isSelected = selectedTeacherSlots.get(row.id)?.has(col.key);
                                const baseBg = rowIndex % 2 === 0 ? '#fff' : '#f9f9f9';
                                return (
                                  <TableCell
                                    key={col.key}
                                    align="center"
                                    sx={{
                                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : baseBg,
                                      borderLeft: isFirstInDay && index > 0 ? '3px solid' : 'none',
                                      borderLeftColor: 'primary.main'
                                    }}
                                  >
                                    {c ? (
                                      <Checkbox
                                        checked={isSelected || false}
                                        onChange={() => handleTeacherSlotToggle(row.id, col.key)}
                                        size="small"
                                        sx={{
                                          p: 0.5,
                                          '&.Mui-checked': { color: 'primary.main' },
                                        }}
                                      />
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                          {(!teacherRows || teacherRows.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={(teacherCols?.length ?? 0) + 1} align="center">
                                No matching slots found. Try removing some filters or widening the time window.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
              )}
            </>
          )}
        </>
      )}

      {!studentId && (
        <Alert severity="info">
          Select an unassigned student above to view batch and teacher availability grids.
        </Alert>
      )}

      <BatchCreationForm
        open={createBatchDialogOpen}
        onClose={() => { setCreateBatchDialogOpen(false); setCreateBatchPrefill(null); }}
        onSuccess={handleCreateBatchSuccess}
        mode="create"
        prefill={createBatchPrefill}
      />
    </Box>
  );
}
